import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { fetchUserData, UserData } from "../lib/database";

export interface UserDataHook {
  userData: UserData | null;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  isLoading: boolean;
  isFetched: boolean;
  hasUserData: boolean;
  refetch: () => void;
}

export function useUserData(): UserDataHook {
  const { user: authUser } = useAuth();

  const {
    data: userData,
    isLoading,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: ["userData", authUser?.id],
    queryFn: () => fetchUserData(authUser!.id),
    enabled: !!authUser?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Memoize the computed values to prevent re-renders
  return useMemo(() => {
    const firstName = userData?.first_name || "";
    const lastName = userData?.last_name || "";
    const fullName = userData?.full_name || `${firstName} ${lastName}`.trim() || "";
    const email = authUser?.email || "";

    return {
      userData: userData || null,
      firstName,
      lastName,
      fullName,
      email,
      isLoading,
      isFetched,
      hasUserData: !!userData,
      refetch,
    };
  }, [userData, authUser?.email, isLoading, isFetched, refetch]);
}
